import { z } from "zod";
import { Form } from "@/app/global-components/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useLocale } from "@/middleware";
import { useTranslate } from "@/locales";
import { routes } from "@/consts";
import ChangePasswordSubmitView from "@/pages/changePassword/components/change-password";
import useChangePassword from "@/hooks/use-change-password";
import ChangePasswordFormFieldView from "@/pages/changePassword/components/change-password-form-field";
import { createChangePasswordSchema } from "@/schema";

const ChangePasswordForm = () => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const ChangePasswordSchema = createChangePasswordSchema(
    t as (key: string) => string
  );
  const navigate = useNavigate();
  const { changePassword, isLoading, error } = useChangePassword();

  const form = useForm<z.infer<typeof ChangePasswordSchema>>({
    resolver: zodResolver(ChangePasswordSchema),
    defaultValues: {
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  const onSubmit = async (values: z.infer<typeof ChangePasswordSchema>) => {
    try {
      await changePassword(values);
      navigate(routes.profile);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <ChangePasswordFormFieldView
          name="oldPassword"
          label={t("authentication.changePassword.fields.oldPassword")}
          placeholder={t(
            "authentication.changePassword.placeholders.enterOldPassword"
          )}
          inputType="password"
          formControl={form.control}
        />
        <ChangePasswordFormFieldView
          name="newPassword"
          label={t("authentication.changePassword.fields.newPassword")}
          placeholder={t(
            "authentication.changePassword.placeholders.enterNewPassword"
          )}
          inputType="password"
          formControl={form.control}
        />
        <ChangePasswordFormFieldView
          name="confirmPassword"
          label={t("authentication.changePassword.fields.confirmNewPassword")}
          placeholder={t(
            "authentication.changePassword.placeholders.enterConfirmNewPassword"
          )}
          inputType="password"
          formControl={form.control}
        />
        {error && (
          <p className="text-red-500 text-sm mt-2">{error}</p>
        )}
        <ChangePasswordSubmitView isLoading={isLoading} />
      </form>
    </Form>
  );
};

export default ChangePasswordForm;
